// Pricing page copy — en / fr / es. EN strings are the source of truth (verbatim from the original page).

export const PRICING_COPY = {
  en: {
    docTitle: "Pricing — Capacity Returned, Measured | Ocean Data",
    eyebrow: "Pricing · Ocean Data",
    h1a: "One clear price.",
    h1b: "Measured capacity back.",
    heroSub:
      "We start with one critical process, make it flow, measure the capacity we create for you — and only then expand. You never pay for our hours. You invest in getting yours back.",
    trialEyebrow: "Every engagement begins the same way",
    trialTitle: "A free Process Mapping Analysis Audit.",
    trialBody:
      "Up to two working meetings where we map one critical process end to end — where the hours leak, where information stalls, and the simplest thing to fix first. You keep the map either way.",
    trialCta: "Book my free audit",
    mostChosen: "Most chosen",
    underCta: "Begins with a free process mapping audit · nothing to cancel",
    tiers: [
      {
        id: "pricing-tier-setup",
        name: "Quick Setup",
        price: "$899 USD",
        priceNote: "≈ $1,250 CAD · plus taxes",
        headline: "Your CRM and marketing, connected in days.",
        body: "A quick CRM and marketing integration setup on the tools you already use: contacts, web forms, campaigns and follow-ups flowing into one place — no platform migration, no new logins to learn.",
        includes: [
          "CRM and marketing tools connected end to end",
          "Web forms and lead capture feeding one pipeline",
          "Follow-up and nurture automations switched on",
          "A clean handoff into your sales process",
        ],
        cta: "Set up my CRM & marketing",
        featured: false,
      },
      {
        id: "pricing-tier-project",
        name: "Project",
        price: "$1,799 USD",
        priceNote: "≈ $2,500 CAD · one process, one payment, plus taxes",
        headline: "One critical process, flowing.",
        body: "Typically the sales-to-project flow, or the books. We pick the process that costs your team the most hours, make it flow, and measure exactly what comes back.",
        includes: [
          "One part of your business, flowing end to end",
          "Clear answers: what's sold, what's done, what's billable",
          "A measured capacity report once it's flowing",
          "Free Process Mapping Analysis Audit before you commit",
        ],
        cta: "Start with one process",
        featured: false,
      },
      {
        id: "pricing-tier-enterprise",
        name: "Enterprise",
        price: "$3,699 USD",
        priceNote: "≈ $5,100 CAD · the whole business, one payment, plus taxes",
        headline: "Sales, projects, and money — finally one system.",
        body: "Your whole flow working from one record, with the routine handoffs happening on their own and every person seeing exactly their part.",
        includes: [
          "Everything in Project",
          "Sales, projects, and the books, connected",
          "Quiet agents that ask before they change anything",
          "Each person's own view of the same truth",
          "Hours returned, measured and reported weekly",
        ],
        cta: "Recover capacity across the business",
        featured: true,
      },
      {
        id: "pricing-tier-custom",
        name: "Custom Implementation",
        price: "$4,779 USD",
        priceNote: "≈ $6,600 CAD · for larger organisations, plus taxes",
        headline: "One simple system, architected for you.",
        body: "A complete way of working, designed around your roles, your approvals, and the tools you already have — for organisations with more moving parts.",
        includes: [
          "One simple system, designed around your roles",
          "Connections to the software you already use",
          "Multi-team information design",
          "An ongoing capacity partnership",
        ],
        cta: "Talk through our architecture",
        featured: false,
      },
    ],
    contrast: [
      { stat: "~11 hrs", text: "the average week one person loses to chasing and re-entering information" },
      { stat: "1 process", text: "where we start — the one quietly costing you the most" },
      { stat: "2 meetings", text: "the free audit that maps your costliest process, before you spend anything" },
    ],
    contrastFoot:
      "If one person wins back even three of those hours, the engagement typically pays for itself inside the first month.",
    faqEyebrow: "Asked before every signature",
    faqTitle: "The honest answers.",
    faqBody:
      "Pricing should lower your heart rate, not raise it. Anything missing here, ask us in the review — no obligation either way.",
    faqs: [
      {
        q: "What is the free Process Mapping Analysis Audit?",
        a: "Up to two working meetings where we map one of your critical processes end to end — where the hours leak, where information stalls, and the simplest thing to fix first. The map is yours to keep whether or not we work together.",
      },
      {
        q: "What is the $899 Quick Setup?",
        a: "A fast CRM and marketing integration on the tools you already use — contacts, forms, campaigns and follow-ups connected end to end. Fixed price, measured in days, and a clean foundation if you later automate a full process with a Project.",
      },
      {
        q: "Why priced per engagement instead of per hour?",
        a: "Because you're not buying our hours — you're buying your own back. A fixed price keeps us honest: the faster we create capacity for you, the better for everyone.",
      },
      {
        q: "What does Enterprise add beyond Project?",
        a: "Project makes one critical process flow. Enterprise connects sales, projects, and finance into one source of truth, with agents that act across all three — follow-ups created, invoices drafted, every role seeing its own view.",
      },
      {
        q: "Do we need to change the software we already use?",
        a: "No. We connect the tools you already run — Excel, QuickBooks, Microsoft 365, Google, Zoho, Autodesk, and the rest — so they behave like one simple system. Your team's daily habits stay; the chasing goes.",
      },
    ],
    stickTitle: "Why the pricing story sticks",
    stick: [
      ["Sticky copy", "You're not buying software hours. You're buying Fridays back."],
      ["Visual idea", "A price tag with a small clock where the dollar sign should be."],
      ["15-second film", "An hourglass on a desk flips itself — and becomes a calendar with empty Friday afternoons."],
      ["Why it sticks", "Contrast does the work: a familiar weekly pain priced against a fixed, reversible decision."],
    ],
    closeTitle: "A free audit. Your real process. Zero risk.",
    closeBody:
      "Watch us map one of your critical processes before you spend a dollar — then decide with the map in your hands.",
    closeCta: "Book my free audit",
    seeJourney: "Want to see the journey first?",
    seeJourneyLink: "See how it works →",
    seeJourneyTo: "/how-it-works",
    demosLink: "Try the live demos →",
    schemaName: "Ocean Data capacity recovery engagements",
    schemaDesc:
      "Business process simplification and AI copilot implementation on Zoho CRM, Projects, and Books — sold as capacity returned, starting with a free Process Mapping Analysis Audit.",
    schemaLang: "en-CA",
    hreflangs: { "en-CA": "/pricing", "fr-CA": "/fr/tarifs", es: "/es/precios", "x-default": "/pricing" },
  },
  fr: {
    docTitle: "Tarifs — Capacité rendue, mesurée | Ocean Data",
    eyebrow: "Tarifs · Ocean Data",
    h1a: "Un prix clair.",
    h1b: "Une capacité mesurée.",
    heroSub:
      "On commence par un processus critique, on le remet en circulation, on mesure la capacité créée — et seulement ensuite on élargit. Vous ne payez jamais nos heures. Vous investissez pour récupérer les vôtres.",
    trialEyebrow: "Chaque mandat commence de la même façon",
    trialTitle: "Un audit d'analyse de processus gratuit.",
    trialBody:
      "Jusqu'à deux rencontres de travail où l'on cartographie un processus critique de bout en bout — où les heures fuient, où l'information s'enlise, et la chose la plus simple à régler d'abord. La carte reste à vous dans tous les cas.",
    trialCta: "Réserver mon audit gratuit",
    mostChosen: "Le plus choisi",
    underCta: "Commence par un audit de cartographie gratuit · rien à annuler",
    tiers: [
      {
        id: "pricing-tier-setup",
        name: "Installation rapide",
        price: "899 $ USD",
        priceNote: "≈ 1 250 $ CAD · plus taxes",
        headline: "Votre CRM et votre marketing, connectés en quelques jours.",
        body: "Une installation rapide d'intégration CRM et marketing sur les outils que vous utilisez déjà : contacts, formulaires web, campagnes et suivis qui coulent vers un seul endroit — sans migration de plateforme, sans nouveaux identifiants à apprendre.",
        includes: [
          "CRM et outils marketing connectés de bout en bout",
          "Formulaires web et capture de prospects vers un seul pipeline",
          "Automatisations de suivi activées",
          "Un passage propre vers votre processus de vente",
        ],
        cta: "Connecter mon CRM et marketing",
        featured: false,
      },
      {
        id: "pricing-tier-project",
        name: "Projet",
        price: "1 799 $ USD",
        priceNote: "≈ 2 500 $ CAD · un processus, un paiement, plus taxes",
        headline: "Un processus critique, remis en circulation.",
        body: "Typiquement le passage vente→projet, ou la comptabilité. On choisit le processus qui coûte le plus d'heures à votre équipe, on le fait couler, et on mesure exactement ce qui revient.",
        includes: [
          "Une partie de votre entreprise, fluide de bout en bout",
          "Des réponses claires : ce qui est vendu, livré, facturable",
          "Un rapport de capacité mesuré une fois le processus en circulation",
          "Audit d'analyse de processus gratuit avant de vous engager",
        ],
        cta: "Commencer par un processus",
        featured: false,
      },
      {
        id: "pricing-tier-enterprise",
        name: "Entreprise",
        price: "3 699 $ USD",
        priceNote: "≈ 5 100 $ CAD · toute l'entreprise, un paiement, plus taxes",
        headline: "Ventes, projets et argent — enfin un seul système.",
        body: "Tout votre flux travaille à partir d'un même dossier, les transferts routiniers se font d'eux-mêmes, et chaque personne voit exactement sa part.",
        includes: [
          "Tout ce que Projet contient",
          "Ventes, projets et comptabilité, connectés",
          "Des agents discrets qui demandent avant de changer quoi que ce soit",
          "La vue de chaque rôle sur la même vérité",
          "Heures rendues, mesurées et rapportées chaque semaine",
        ],
        cta: "Récupérer de la capacité partout",
        featured: true,
      },
      {
        id: "pricing-tier-custom",
        name: "Sur mesure",
        price: "4 779 $ USD",
        priceNote: "≈ 6 600 $ CAD · pour les organisations plus grandes, plus taxes",
        headline: "Un système simple, architecturé pour vous.",
        body: "Une façon complète de travailler, pensée autour de vos rôles, vos approbations et les outils que vous avez déjà — pour les organisations avec plus de pièces en mouvement.",
        includes: [
          "Un système simple, conçu autour de vos rôles",
          "Connexions aux logiciels que vous utilisez déjà",
          "Conception de l'information multi-équipes",
          "Un partenariat capacité continu",
        ],
        cta: "Parler de notre architecture",
        featured: false,
      },
    ],
    contrast: [
      { stat: "~11 h", text: "la semaine moyenne qu'une personne perd à courir après l'information et à la ressaisir" },
      { stat: "1 processus", text: "par où on commence — celui qui vous coûte le plus, tranquillement" },
      { stat: "2 rencontres", text: "l'audit gratuit qui cartographie le processus qui vous coûte le plus, avant de payer" },
    ],
    contrastFoot:
      "Si une seule personne récupère ne serait-ce que trois de ces heures, le mandat se rembourse typiquement dans le premier mois.",
    faqEyebrow: "Demandé avant chaque signature",
    faqTitle: "Les réponses honnêtes.",
    faqBody:
      "Un prix devrait calmer le rythme cardiaque, pas l'accélérer. S'il manque quelque chose ici, demandez-le dans la revue — sans obligation.",
    faqs: [
      {
        q: "Qu'est-ce que l'audit d'analyse de processus gratuit ?",
        a: "Jusqu'à deux rencontres de travail où l'on cartographie un de vos processus critiques de bout en bout — où les heures fuient, où l'information s'enlise, et la chose la plus simple à régler d'abord. La carte reste à vous, que l'on travaille ensemble ou non.",
      },
      {
        q: "C'est quoi l'installation rapide à 899 $ ?",
        a: "Une intégration rapide de votre CRM et de votre marketing sur les outils que vous avez déjà — contacts, formulaires, campagnes et suivis connectés de bout en bout. Prix fixe, compté en jours, et une base propre si vous automatisez ensuite un processus complet avec un Projet.",
      },
      {
        q: "Pourquoi un prix par mandat plutôt qu'à l'heure ?",
        a: "Parce que vous n'achetez pas nos heures — vous rachetez les vôtres. Un prix fixe nous garde honnêtes : plus vite on crée de la capacité pour vous, mieux c'est pour tout le monde.",
      },
      {
        q: "Qu'est-ce qu'Entreprise ajoute à Projet ?",
        a: "Projet fait couler un processus critique. Entreprise connecte ventes, projets et finances en une seule source de vérité, avec des agents qui agissent sur les trois — suivis créés, factures préparées, chaque rôle voyant sa propre vue.",
      },
      {
        q: "Devons-nous changer les logiciels qu'on utilise déjà ?",
        a: "Non. On connecte les outils que vous utilisez — Excel, QuickBooks, Microsoft 365, Google, Zoho, Autodesk et les autres — pour qu'ils se comportent comme un seul système simple. Les habitudes de votre équipe restent; la poursuite s'en va.",
      },
    ],
    stickTitle: "Pourquoi le message de prix colle",
    stick: [
      ["Phrase qui colle", "Vous n'achetez pas des heures de logiciel. Vous rachetez vos vendredis."],
      ["Idée visuelle", "Une étiquette de prix avec une petite horloge là où le signe de dollar devrait être."],
      ["Film de 15 secondes", "Un sablier sur un bureau se retourne tout seul — et devient un calendrier aux vendredis après-midi vides."],
      ["Pourquoi ça colle", "Le contraste fait le travail : une douleur hebdomadaire familière face à une décision fixe et réversible."],
    ],
    closeTitle: "Un audit gratuit. Votre vrai processus. Zéro risque.",
    closeBody:
      "Regardez-nous cartographier un de vos processus critiques avant de dépenser un dollar — puis décidez, la carte en main.",
    closeCta: "Réserver mon audit gratuit",
    seeJourney: "Voir le parcours d'abord ?",
    seeJourneyLink: "Voir comment ça marche →",
    seeJourneyTo: "/fr/comment-ca-marche",
    demosLink: "Essayer les démos en direct →",
    schemaName: "Mandats de récupération de capacité Ocean Data",
    schemaDesc:
      "Simplification des processus d'affaires et connexion des outils existants — vendue comme capacité rendue, à partir d'un audit d'analyse de processus gratuit.",
    schemaLang: "fr-CA",
    hreflangs: { "en-CA": "/pricing", "fr-CA": "/fr/tarifs", es: "/es/precios", "x-default": "/pricing" },
  },
  es: {
    docTitle: "Precios — Capacidad devuelta, medida | Ocean Data",
    eyebrow: "Precios · Ocean Data",
    h1a: "Un precio claro.",
    h1b: "Capacidad medida de vuelta.",
    heroSub:
      "Empezamos con un proceso crítico, lo ponemos a fluir, medimos la capacidad que creamos para usted — y solo entonces ampliamos. Nunca paga por nuestras horas. Invierte en recuperar las suyas.",
    trialEyebrow: "Cada proyecto empieza igual",
    trialTitle: "Una auditoría gratuita de análisis y mapeo de procesos.",
    trialBody:
      "Hasta dos reuniones de trabajo donde mapeamos un proceso crítico de punta a punta — dónde se escapan las horas, dónde se estanca la información y lo más simple de arreglar primero. El mapa es suyo en cualquier caso.",
    trialCta: "Reservar mi auditoría gratuita",
    mostChosen: "El más elegido",
    underCta: "Empieza con una auditoría de mapeo gratuita · nada que cancelar",
    tiers: [
      {
        id: "pricing-tier-setup",
        name: "Configuración rápida",
        price: "$899 USD",
        priceNote: "≈ $1,250 CAD · más impuestos",
        headline: "Su CRM y su marketing, conectados en días.",
        body: "Una configuración rápida de integración de CRM y marketing sobre las herramientas que ya usa: contactos, formularios web, campañas y seguimientos fluyendo a un solo lugar — sin migración de plataforma, sin nuevos accesos que aprender.",
        includes: [
          "CRM y herramientas de marketing conectados de punta a punta",
          "Formularios web y captura de prospectos hacia un solo pipeline",
          "Automatizaciones de seguimiento activadas",
          "Un traspaso limpio a su proceso de ventas",
        ],
        cta: "Conectar mi CRM y marketing",
        featured: false,
      },
      {
        id: "pricing-tier-project",
        name: "Proyecto",
        price: "$1,799 USD",
        priceNote: "≈ $2,500 CAD · un proceso, un pago, más impuestos",
        headline: "Un proceso crítico, fluyendo.",
        body: "Típicamente el paso de venta a proyecto, o la contabilidad. Elegimos el proceso que más horas le cuesta a su equipo, lo ponemos a fluir y medimos exactamente lo que vuelve.",
        includes: [
          "Una parte de su empresa, fluida de punta a punta",
          "Respuestas claras: qué está vendido, hecho, facturable",
          "Un informe de capacidad medido una vez que fluye",
          "Auditoría de análisis de procesos gratuita antes de comprometerse",
        ],
        cta: "Empezar con un proceso",
        featured: false,
      },
      {
        id: "pricing-tier-enterprise",
        name: "Empresa",
        price: "$3,699 USD",
        priceNote: "≈ $5,100 CAD · toda la empresa, un pago, más impuestos",
        headline: "Ventas, proyectos y dinero — por fin un solo sistema.",
        body: "Todo su flujo trabajando desde un mismo expediente, con los traspasos rutinarios ocurriendo solos y cada persona viendo exactamente su parte.",
        includes: [
          "Todo lo que incluye Proyecto",
          "Ventas, proyectos y contabilidad, conectados",
          "Agentes discretos que piden permiso antes de cambiar nada",
          "La vista de cada rol sobre la misma verdad",
          "Horas devueltas, medidas y reportadas cada semana",
        ],
        cta: "Recuperar capacidad en toda la empresa",
        featured: true,
      },
      {
        id: "pricing-tier-custom",
        name: "A medida",
        price: "$4,779 USD",
        priceNote: "≈ $6,600 CAD · para organizaciones más grandes, más impuestos",
        headline: "Un sistema simple, diseñado para usted.",
        body: "Una forma completa de trabajar, pensada alrededor de sus roles, sus aprobaciones y las herramientas que ya tiene — para organizaciones con más piezas en movimiento.",
        includes: [
          "Un sistema simple, diseñado alrededor de sus roles",
          "Conexiones al software que ya usa",
          "Diseño de información multi-equipo",
          "Una alianza de capacidad continua",
        ],
        cta: "Conversar sobre nuestra arquitectura",
        featured: false,
      },
    ],
    contrast: [
      { stat: "~11 h", text: "la semana promedio que una persona pierde persiguiendo y reingresando información" },
      { stat: "1 proceso", text: "donde empezamos — el que silenciosamente más le cuesta" },
      { stat: "2 reuniones", text: "la auditoría gratuita que mapea el proceso que más le cuesta, antes de pagar" },
    ],
    contrastFoot:
      "Si una sola persona recupera aunque sea tres de esas horas, el proyecto típicamente se paga solo dentro del primer mes.",
    faqEyebrow: "Preguntado antes de cada firma",
    faqTitle: "Las respuestas honestas.",
    faqBody:
      "Un precio debería bajarle el pulso, no subirlo. Si falta algo aquí, pregúntelo en la revisión — sin compromiso.",
    faqs: [
      {
        q: "¿Qué es la auditoría gratuita de análisis y mapeo de procesos?",
        a: "Hasta dos reuniones de trabajo donde mapeamos uno de sus procesos críticos de punta a punta — dónde se escapan las horas, dónde se estanca la información y lo más simple de arreglar primero. El mapa es suyo, trabajemos juntos o no.",
      },
      {
        q: "¿Qué es la configuración rápida de $899?",
        a: "Una integración rápida de su CRM y marketing sobre las herramientas que ya tiene — contactos, formularios, campañas y seguimientos conectados de punta a punta. Precio fijo, medido en días, y una base limpia si luego automatiza un proceso completo con un Proyecto.",
      },
      {
        q: "¿Por qué precio por proyecto en vez de por hora?",
        a: "Porque usted no compra nuestras horas — compra las suyas de vuelta. Un precio fijo nos mantiene honestos: mientras más rápido creamos capacidad para usted, mejor para todos.",
      },
      {
        q: "¿Qué añade Empresa sobre Proyecto?",
        a: "Proyecto pone a fluir un proceso crítico. Empresa conecta ventas, proyectos y finanzas en una sola fuente de verdad, con agentes que actúan en los tres — seguimientos creados, facturas preparadas, cada rol viendo su propia vista.",
      },
      {
        q: "¿Tenemos que cambiar el software que ya usamos?",
        a: "No. Conectamos las herramientas que ya usa — Excel, QuickBooks, Microsoft 365, Google, Zoho, Autodesk y las demás — para que se comporten como un solo sistema simple. Los hábitos de su equipo se quedan; la persecución se va.",
      },
    ],
    stickTitle: "Por qué el mensaje de precio se queda",
    stick: [
      ["Frase que se pega", "No compra horas de software. Compra sus viernes de vuelta."],
      ["Idea visual", "Una etiqueta de precio con un pequeño reloj donde debería estar el signo de dólar."],
      ["Filme de 15 segundos", "Un reloj de arena sobre un escritorio se voltea solo — y se vuelve un calendario con los viernes por la tarde vacíos."],
      ["Por qué se pega", "El contraste hace el trabajo: un dolor semanal conocido frente a una decisión fija y reversible."],
    ],
    closeTitle: "Una auditoría gratuita. Su proceso real. Cero riesgo.",
    closeBody:
      "Vea cómo mapeamos uno de sus procesos críticos antes de gastar un dólar — luego decida con el mapa en mano.",
    closeCta: "Reservar mi auditoría gratuita",
    seeJourney: "¿Quiere ver el recorrido primero?",
    seeJourneyLink: "Vea cómo funciona →",
    seeJourneyTo: "/es/como-funciona",
    demosLink: "Probar las demos en vivo →",
    schemaName: "Proyectos de recuperación de capacidad Ocean Data",
    schemaDesc:
      "Simplificación de procesos de negocio y conexión de herramientas existentes — vendida como capacidad devuelta, comenzando con una auditoría gratuita de análisis y mapeo de procesos.",
    schemaLang: "es",
    hreflangs: { "en-CA": "/pricing", "fr-CA": "/fr/tarifs", es: "/es/precios", "x-default": "/pricing" },
  },
};
